import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import "./ProfileAddressUpdateModal.scss";
import axios from "axios";
import { connect } from "react-redux";
import authActions from "../../../../../redux/auth/action";
import userPreferenceActions from "../../../../../redux/user_preference/action";
import uiActions from "../../../../../redux/ui/action";
import { url_3, url_4, url_6 } from "../../../../../custom_utilities/api_services";
import { isEmpty } from "../../../../../custom_utilities/custom_useful_functions";
import {
  PROFILE_PAYMENT_FIELDS,
  getProfileFieldByLabel,
  inferMissingFieldsFromApiError,
  isDobInvalid,
  isFieldMissing,
} from "../../../../../custom_utilities/profilePaymentFields";

const EMPTY_FORM_VALUES = {
  name: "",
  mobile: "",
  email: "",
  dob: "",
  gender: "",
  state: "",
  city: "",
};

const ProfileAddressUpdateModal = (props) => {
  const {
    isModalOpen,
    closeModal,
    missingFields = [],
    user,
    userPreference,
    setUser,
    setUserPreference,
    startLoading,
    finishLoading,
    onProfileUpdated,
  } = props;

  const [formValues, setFormValues] = useState(EMPTY_FORM_VALUES);
  const [activeMissingFields, setActiveMissingFields] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");

  const needsStateOrCity = activeMissingFields.some((label) =>
    ["state", "city"].includes(label)
  );

  useEffect(() => {
    if (!isModalOpen) return;

    setActiveMissingFields(missingFields);
    setFormValues({
      name: user?.name || "",
      mobile: user?.mobile || "",
      email: user?.email || "",
      dob: user?.dob || "",
      gender: user?.gender || "Male",
      state: user?.address?.state || "",
      city: user?.address?.city || "",
    });
    setErrorMsg("");

    if (missingFields.some((label) => label === "state" || label === "city")) {
      fetchStates();
    }

    if (user?.address?.state) {
      fetchCities(user.address.state);
    } else {
      setCities([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, missingFields, user]);

  const fetchStates = async () => {
    if (!window.navigator.onLine) return;

    try {
      const response = await axios.get(url_3);
      if (response.data.status === 200) {
        setStates(response.data.data?.states || []);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCities = async (selectedState) => {
    if (!selectedState || !window.navigator.onLine) return;

    startLoading();
    try {
      const response = await axios.get(`${url_4}?state=${selectedState}`);
      if (response.data.status === 200) {
        setCities(response.data.data?.districts || []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      finishLoading();
    }
  };

  const handleStateChange = (event) => {
    const { value } = event.target;
    setFormValues((prev) => ({ ...prev, state: value, city: "" }));
    setCities([]);
    fetchCities(value);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const getResolvedValue = (formKey) => {
    const fieldConfig = PROFILE_PAYMENT_FIELDS.find(
      (field) => field.formKey === formKey
    );
    if (!fieldConfig) return "";

    if (activeMissingFields.includes(fieldConfig.label)) {
      return formValues[formKey];
    }

    if (formKey === "state") return user?.address?.state || "";
    if (formKey === "city") return user?.address?.city || "";
    return user?.[formKey] || "";
  };

  const validateForm = () => {
    for (const label of activeMissingFields) {
      const fieldConfig = getProfileFieldByLabel(label);
      if (!fieldConfig) continue;

      const value = formValues[fieldConfig.formKey];

      if (fieldConfig.formKey === "dob" && isDobInvalid(value)) {
        return "Please enter a valid date of birth";
      }

      if (
        fieldConfig.formKey === "email" &&
        (isFieldMissing(value) ||
          !/^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
            String(value).trim()
          ))
      ) {
        return "Please enter a valid email";
      }

      if (
        fieldConfig.formKey !== "dob" &&
        fieldConfig.formKey !== "email" &&
        isFieldMissing(value)
      ) {
        return `Please enter ${label}`;
      }
    }

    return "";
  };

  const updateProfile = async () => {
    const validationError = validateForm();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    if (!window.navigator.onLine) {
      setErrorMsg("Please check your internet connection");
      return;
    }

    const targetId =
      userPreference?.targetDetails?.[0]?.target_id ||
      user?.UsertargetDetail?.[0]?.target_id;
    const courseId =
      userPreference?.courseDetails?.[0]?.course_id ||
      user?.targets?.[0]?.course_id;
    const classId = userPreference?.classDetails?.class_id || user?.class_id;
    const batchId = userPreference?.batchDetails?.batch_id || user?.batch_id;

    const formData = new FormData();
    formData.append("name", getResolvedValue("name"));
    formData.append("dob", getResolvedValue("dob"));
    formData.append("gender", getResolvedValue("gender") || "Male");
    formData.append("roll_no", user?.roll_no || "");
    formData.append("state", getResolvedValue("state"));
    formData.append("mobile_number", getResolvedValue("mobile"));
    formData.append("city", getResolvedValue("city"));

    const resolvedEmail = getResolvedValue("email");
    if (resolvedEmail) {
      formData.append("email", resolvedEmail);
    } else if (user?.email) {
      formData.append("email", user.email);
    }

    if (user?.acps_pin >= 1000) {
      formData.append("acps_pin", user.acps_pin);
    }

    if (targetId) {
      formData.append("user_target_id", targetId);
    }
    if (courseId) {
      formData.append("target_ids", JSON.stringify([courseId]));
    }
    if (classId) {
      formData.append("class_id", classId);
    }
    if (batchId) {
      formData.append("batch_id", batchId);
    }

    startLoading();
    try {
      const response = await axios.post(url_6, formData);
      if (response.status === 200 && response.data.status === 200) {
        const apiData = response.data.data;
        setUser(apiData);
        setUserPreference({
          targetDetails: apiData.UsertargetDetail,
          courseDetails: apiData.targets,
          classDetails: {
            class_id: apiData.class_id,
            class: apiData.class,
          },
          batchDetails: {
            batch_id: apiData.batch_id,
            batch: apiData.batch,
          },
        });
        closeModal();
        if (onProfileUpdated) {
          onProfileUpdated(apiData);
        }
      } else {
        const message = response.data.message || "Something went wrong";
        setActiveMissingFields((prev) =>
          inferMissingFieldsFromApiError(message, prev)
        );
        setErrorMsg(message);
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.statusText ||
        "Something went wrong";
      setActiveMissingFields((prev) =>
        inferMissingFieldsFromApiError(message, prev)
      );
      setErrorMsg(message);
    } finally {
      finishLoading();
    }
  };

  const renderField = (label) => {
    switch (label) {
      case "name":
        return (
          <div className="profile-field" key={label}>
            <label htmlFor="profile-name">Name</label>
            <input
              id="profile-name"
              type="text"
              name="name"
              placeholder="Enter Name"
              value={formValues.name}
              onChange={handleInputChange}
            />
          </div>
        );
      case "mobile number":
        return (
          <div className="profile-field" key={label}>
            <label htmlFor="profile-mobile">Mobile</label>
            <input
              id="profile-mobile"
              type="text"
              name="mobile"
              placeholder="Enter Mobile Number"
              value={formValues.mobile}
              onChange={handleInputChange}
            />
          </div>
        );
      case "email":
        return (
          <div className="profile-field" key={label}>
            <label htmlFor="profile-email">Email id</label>
            <input
              id="profile-email"
              type="email"
              name="email"
              placeholder="Enter Email ID"
              value={formValues.email}
              onChange={handleInputChange}
            />
          </div>
        );
      case "date of birth":
        return (
          <div className="profile-field" key={label}>
            <label htmlFor="profile-dob">Date of Birth</label>
            <input
              id="profile-dob"
              type="date"
              name="dob"
              value={formValues.dob}
              onChange={handleInputChange}
            />
          </div>
        );
      case "gender":
        return (
          <div className="profile-field" key={label}>
            <label htmlFor="profile-gender">Gender</label>
            <select
              id="profile-gender"
              name="gender"
              value={formValues.gender}
              onChange={handleInputChange}
            >
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>
        );
      case "state":
        return (
          <div className="profile-field" key={label}>
            <label htmlFor="profile-state">State</label>
            <select
              id="profile-state"
              name="state"
              value={formValues.state}
              onChange={handleStateChange}
            >
              <option value="" disabled>
                Select State
              </option>
              {!isEmpty(states)
                ? states.map((element, index) => (
                    <option key={index} value={element}>
                      {element}
                    </option>
                  ))
                : null}
            </select>
          </div>
        );
      case "city":
        return (
          <div className="profile-field" key={label}>
            <label htmlFor="profile-city">City</label>
            <select
              id="profile-city"
              name="city"
              value={formValues.city}
              onChange={handleInputChange}
              disabled={!formValues.state}
            >
              <option value="" disabled>
                {formValues.state ? "Select City" : "Select State first"}
              </option>
              {!isEmpty(cities)
                ? cities.map((element, index) => (
                    <option key={index} value={element}>
                      {element}
                    </option>
                  ))
                : null}
            </select>
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (
      isModalOpen &&
      needsStateOrCity &&
      states.length === 0
    ) {
      fetchStates();
    }
  }, [isModalOpen, needsStateOrCity, states.length]);

  return (
    <Modal
      show={isModalOpen}
      onHide={closeModal}
      dialogClassName="profile-address-update-wrapper"
      centered
    >
      <Modal.Header closeButton className="profile-address-modal-header" />

      <Modal.Body>
        <div className="profile-address-details">
          <h2>
            Please update the following in your profile before
            <br /> proceeding to payment: {activeMissingFields.join(", ")}
          </h2>

          <div className="profile-address-fields">
            {activeMissingFields.map((label) => renderField(label))}
          </div>

          {errorMsg ? <div className="profile-error-text">{errorMsg}</div> : null}

          <div className="update-button" onClick={updateProfile}>
            <button type="button">Update Changes</button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
  userPreference: state.userPreference,
});

const mapDispatchToProps = (dispatch) => ({
  setUser: (payload) => dispatch(authActions.setUser(payload)),
  setUserPreference: (payload) =>
    dispatch(userPreferenceActions.setUserPreference(payload)),
  startLoading: () => dispatch(uiActions.startLoading()),
  finishLoading: () => dispatch(uiActions.finishLoading()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProfileAddressUpdateModal);
